<script setup>
import { computed, onMounted, ref, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import RecordingsAPI from 'dashboard/api/recordings';
const route = useRoute();

const recordings = ref([]);
const loading = ref(false);
const error = ref('');
const playingId = ref(null);
const audioUrl = ref('');
const dateFrom = ref('');
const dateTo = ref('');
const numberFilter = ref('');

const page = ref(1);
const perPage = ref(25);
const total = ref(0);
const totalPages = ref(1);
const accountId = computed(() => route.params.accountId);

const formatDuration = seconds => {
  const value = Number(seconds || 0);
  const minutes = Math.floor(value / 60);
  const secs = value % 60;

  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const formatDate = value => {
  if (!value) return '';

  const date = new Date(value.replace(' ', 'T'));

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(date);
};
const loadRecordings = async () => {
  loading.value = true;
  error.value = '';

  try {
    const params = {
      page: page.value,
      per_page: perPage.value,
    };

    if (dateFrom.value) {
      params.date_from = dateFrom.value;
    }

    if (dateTo.value) {
      params.date_to = dateTo.value;
    }

    if (numberFilter.value) {
      params.number = numberFilter.value.trim();
    }

    const response = await RecordingsAPI.getRecordings(params);

    recordings.value = response.data?.recordings || [];
    total.value = response.data?.total || 0;
    totalPages.value = response.data?.total_pages || 1;
  } catch (err) {
    console.error('Erro ao carregar gravações:', err);
    error.value = 'Não foi possível carregar as gravações.';
  } finally {
    loading.value = false;
  }
};
const playRecording = async recording => {
  try {
    if (audioUrl.value) {
      URL.revokeObjectURL(audioUrl.value);
    }

    playingId.value = recording.uniqueid;

    const response = await RecordingsAPI.getAudio(
      recording.uniqueid
    );

    audioUrl.value = URL.createObjectURL(response.data);
  } catch (err) {
    console.error('Erro ao reproduzir gravação:', err);
    error.value = 'Não foi possível reproduzir a gravação.';
  }
};

const downloadRecording = async recording => {
  try {
    const response = await RecordingsAPI.download(
      recording.uniqueid
    );

    const url = URL.createObjectURL(response.data);

    const link = document.createElement('a');
    link.href = url;
    link.download =
      recording.recordingfile ||
      `gravacao-${recording.uniqueid}.wav`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Erro ao baixar gravação:', err);
    error.value = 'Não foi possível baixar a gravação.';
  }
};
const searchRecordings = () => {
  page.value = 1;
  loadRecordings();
};

const clearFilters = () => {
  dateFrom.value = '';
  dateTo.value = '';
  numberFilter.value = '';
  page.value = 1;
  loadRecordings();
};

const previousPage = () => {
  if (page.value <= 1) return;

  page.value -= 1;
  loadRecordings();
};

const nextPage = () => {
  if (page.value >= totalPages.value) return;

  page.value += 1;
  loadRecordings();
};
onBeforeUnmount(() => {
  if (audioUrl.value) {
    URL.revokeObjectURL(audioUrl.value);
  }
});
onMounted(() => {
  loadRecordings();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <div class="flex items-center justify-between px-8 py-6 border-b border-n-weak">
      <div>
        <h1 class="text-2xl font-semibold text-n-slate-12">
          Gravações
        </h1>

        <p class="mt-1 text-sm text-n-slate-11">
          Ouça e baixe as gravações associadas ao seu ramal.
        </p>
      </div>

      <button
        type="button"
        class="px-4 py-2 text-sm font-medium rounded-lg bg-n-alpha-black2 text-n-slate-12 hover:bg-n-alpha-black3"
        @click="loadRecordings"
      >
        Atualizar
      </button>
    </div>
<div class="px-8 py-4 border-b border-n-weak">
  <div class="flex flex-wrap items-end gap-4">
    <div>
      <label class="block mb-1 text-sm text-n-slate-11">
        De
      </label>

      <input
        v-model="dateFrom"
        type="date"
        class="px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
      />
    </div>

    <div>
      <label class="block mb-1 text-sm text-n-slate-11">
        Até
      </label>

      <input
        v-model="dateTo"
        type="date"
        class="px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
      />
    </div>

    <div>
      <label class="block mb-1 text-sm text-n-slate-11">
        Número / Ramal
      </label>

      <input
        v-model="numberFilter"
        type="text"
        placeholder="Ex.: 100"
        class="px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
        @keyup.enter="searchRecordings"
      />
    </div>

    <button
      type="button"
      class="px-4 py-2 font-medium text-white rounded-lg bg-n-brand"
      @click="searchRecordings"
    >
      Buscar
    </button>

    <button
      type="button"
      class="px-4 py-2 font-medium rounded-lg bg-n-alpha-black2 text-n-slate-12"
      @click="clearFilters"
    >
      Limpar
    </button>
  </div>
</div>
    <div class="flex-1 p-8 overflow-auto">
      <div
        v-if="loading"
        class="py-12 text-center text-n-slate-11"
      >
        Carregando gravações...
      </div>

      <div
        v-else-if="error"
        class="p-4 rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        {{ error }}
      </div>

      <div
        v-else-if="recordings.length === 0"
        class="py-12 text-center text-n-slate-11"
      >
        Nenhuma gravação encontrada.
      </div>

      <div
        v-else
        class="overflow-hidden border rounded-xl border-n-weak bg-n-background"
      >
        <table class="w-full text-sm">
          <thead class="bg-n-alpha-black2 text-n-slate-11">
            <tr>
              <th class="px-4 py-3 text-left">
                Data / Hora
              </th>

              <th class="px-4 py-3 text-left">
                Origem
              </th>

              <th class="px-4 py-3 text-left">
                Destino
              </th>

              <th class="px-4 py-3 text-left">
                Duração
              </th>

              <th class="px-4 py-3 text-left">
                Ações
              </th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="recording in recordings"
              :key="recording.uniqueid"
              class="border-t border-n-weak"
            >
              <td class="px-4 py-4 text-n-slate-12">
                {{ formatDate(recording.calldate) }}
              </td>

              <td class="px-4 py-4 text-n-slate-12">
                {{ recording.src }}
              </td>

              <td class="px-4 py-4 text-n-slate-12">
                {{ recording.dst }}
              </td>

              <td class="px-4 py-4 text-n-slate-12">
                {{ formatDuration(recording.billsec) }}
              </td>

              <td class="px-4 py-4">
                <div class="flex items-center gap-2">
                  <button
                    type="button"
                    class="px-3 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:opacity-90"
                    @click="playRecording(recording)"
                  >
                    ▶ Ouvir
                  </button>

                  <button
                    type="button"
                    class="px-3 py-2 text-sm font-medium rounded-lg bg-n-alpha-black2 text-n-slate-12 hover:bg-n-alpha-black3"
                    @click="downloadRecording(recording)"
                  >
                    ⬇ Baixar
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="audioUrl"
          class="p-4 border-t border-n-weak"
        >
          <div class="mb-2 text-sm text-n-slate-11">
            Reproduzindo gravação
          </div>
<div
  v-if="total > 0"
  class="flex items-center justify-between px-4 py-4 border-t border-n-weak"
>
  <div class="text-sm text-n-slate-11">
    {{ total }} gravação(ões)
  </div>

  <div class="flex items-center gap-3">
    <button
      type="button"
      :disabled="page <= 1"
      class="px-3 py-2 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 disabled:opacity-40"
      @click="previousPage"
    >
      ← Anterior
    </button>

    <span class="text-sm text-n-slate-11">
      Página {{ page }} de {{ totalPages }}
    </span>

    <button
      type="button"
      :disabled="page >= totalPages"
      class="px-3 py-2 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 disabled:opacity-40"
      @click="nextPage"
    >
      Próxima →
    </button>
  </div>
</div>
          <audio
            :key="playingId"
            :src="audioUrl"
            controls
            autoplay
            class="w-full"
          />
        </div>
      </div>
    </div>
  </div>
</template>
